// TSL Block Studio — author / test / publish a Safe-TSL block. Replaces the raw-JS editor for public
// user blocks. Two synchronized modes (Visual form ↔ TSL text), both editing the SAME canonical AST; live
// validation with budget usage; a preview workspace running the controlled interpreter; and the
// draft → gate → publish / fork flow. No JavaScript is ever entered or emitted.

import React, { useMemo, useState } from 'react';
import { Boxes, Code2, Play, UploadCloud, X, GitFork, CheckCircle2, AlertTriangle } from 'lucide-react';
import { printBlock, categoryForBlockType } from '../../engine/tslBlock/blockText';
import { validateStudioText, gateStudioBlock, type StudioValidation } from '../../engine/tslBlock/studioGate';
import { createInterpreter } from '../../engine/tslBlock/interpret';
import { createDraft, publishBlock } from '../../services/customBlockLibraryClient';
import type { CustomBlockSubtype, GateReport } from '../../types/customBlockLibrary';

const STARTERS: Record<string, string> = {
  indicator: `indicator {\n  param period: int 5..100 = 20\n  value: ((close / ema(close, period)) - 1)\n  signal: (((close / ema(close, period)) - 1) > 0)\n}`,
  filter: `filter {\n  param fast: int 2..50 = 10\n  param slow: int 5..200 = 30\n  signal: crossover(ema(close, fast), ema(close, slow))\n}`,
  qty_calculator: `qty_calculator {\n  param frac: float 0.001..0.1 = 0.01\n  qty: ((equity * frac) / entryPrice)\n}`,
  risk_rule: `risk_rule {\n  param maxBars: int 1..500 = 50\n  risk: ((barsSinceEntry >= maxBars) ? 2 : 0)\n}`,
};

function synth(n: number) { const o = []; const t0 = 1700000000000; for (let i = 0; i < n; i++) { const p = 100 + 16 * Math.sin(i / 13) + 5 * Math.sin(i / 3); o.push({ open: p - 0.1, high: p + 0.6, low: p - 0.6, close: p, volume: 1000, timestamp: t0 + i * 3600000 }); } return o; }

interface Props { initialText?: string; fork?: { id: string; version: number }; onClose: () => void; }

export const TslBlockStudio: React.FC<Props> = ({ initialText, fork, onClose }) => {
  const [mode, setMode] = useState<'visual' | 'text'>('text');
  const [text, setText] = useState(initialText ?? STARTERS.indicator);
  const [name, setName] = useState('My block');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [preview, setPreview] = useState<{ value: number[]; signals: boolean[] } | null>(null);
  const [report, setReport] = useState<GateReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [pubErr, setPubErr] = useState<string | null>(null);

  const v: StudioValidation = useMemo(() => validateStudioText(text), [text]);
  const blockType = (v.block?.blockType ?? 'indicator') as CustomBlockSubtype;
  const category = categoryForBlockType(blockType);

  // keep the textarea canonical when valid (visual ↔ text sync via the AST)
  const reformat = () => { if (v.block) setText(printBlock(v.block)); };

  const runPreview = () => {
    if (!v.block) return;
    const md = synth(300);
    const params: Record<string, unknown> = {}; for (const [k, s] of Object.entries(v.block.params)) params[k] = s.default;
    if (category === 'sizing') { const q = createInterpreter(v.block).qty({ equity: 100000, dayStartEquity: 100000, initialCapital: 100000, entryPrice: 100, stopDistance: 2, atr: 1.5, price: 100 }, params); setPreview({ value: [q], signals: [] }); }
    else if (category === 'risk_rules') { const acts = createInterpreter(v.block).risk(md, params); setPreview({ value: acts.map((a) => (a ? 1 : 0)), signals: acts.map((a) => !!a) }); }
    else { setPreview(createInterpreter(v.block).series(md, params)); }
  };

  const onPublish = async () => {
    if (!v.block) return;
    setBusy(true); setPubErr(null); setReport(null);
    try {
      const g = await gateStudioBlock(v.block); setReport(g.report);
      if (!g.report.publicVisible.passed) { setBusy(false); return; }
      if (!description.trim()) { setPubErr('a description is required'); setBusy(false); return; }
      const tagList = tags.split(',').map((t) => t.trim()).filter(Boolean);
      const blockId = await createDraft({ category, blockType, name: name.trim(), description: description.trim(), tags: tagList, forkedFrom: fork });
      if (!blockId) { setPubErr('could not create draft (signed in + verified?)'); setBusy(false); return; }
      const res = await publishBlock(blockId, { category, blockType, name: name.trim(), description: description.trim(), tags: tagList, tslAst: v.block, paramSchema: v.block.params } as never);
      if (res.ok && res.data?.accept) setDone(`Published v${res.data.version}.`);
      else setPubErr(res.data?.reasons?.join('; ') ?? res.data?.error ?? `publish failed (${res.status})`);
    } catch (e) { setPubErr(String((e as Error)?.message ?? e)); }
    finally { setBusy(false); }
  };

  const errs = [...v.parseErrors.map((e) => `line ${e.line}: ${e.error}`), ...v.validateErrors];
  const budget = v.budget;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800"><Boxes className="w-4 h-4" /> TSL Block Studio {fork && <span className="text-xs font-normal text-gray-400 inline-flex items-center gap-1"><GitFork className="w-3 h-3" /> fork of {fork.id}@v{fork.version}</span>}</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        {done ? (
          <div className="p-6 flex items-start gap-2 text-sm text-emerald-800"><CheckCircle2 className="w-5 h-5 shrink-0" /><div><b>{done}</b> Your TSL block is public. It runs through the controlled engine — no JavaScript involved.</div></div>
        ) : (
          <>
            <div className="flex items-center gap-2 px-4 py-2 border-b text-xs">
              <button onClick={() => setMode('visual')} className={`px-2 py-1 rounded ${mode === 'visual' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600'}`}>Visual</button>
              <button onClick={() => setMode('text')} className={`px-2 py-1 rounded inline-flex items-center gap-1 ${mode === 'text' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600'}`}><Code2 className="w-3 h-3" /> TSL</button>
              <select value={blockType} onChange={(e) => setText(STARTERS[e.target.value] ?? STARTERS.indicator)} className="ml-2 border rounded px-1 py-0.5">
                {['indicator', 'filter', 'qty_calculator', 'risk_rule'].map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <span className="ml-auto text-gray-400">category: <b>{category}</b></span>
            </div>

            <div className="overflow-y-auto p-4 space-y-3">
              {mode === 'text' ? (
                <>
                  <textarea value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} rows={12} className="w-full font-mono text-xs border border-gray-300 rounded p-2 bg-gray-50" />
                  <button onClick={reformat} disabled={!v.block} className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-40">Format (canonical)</button>
                </>
              ) : (
                <div className="text-xs text-gray-600 space-y-2">
                  <div className="text-gray-400">Visual builder edits the same canonical block. Parameters + section expressions below; switch to <b>TSL</b> for the full text. (A drag-and-drop node canvas is the next iteration; expressions use the safe TSL operations: sources, params, arithmetic, comparisons, logic, <code>ema/sma/rsi/atr…</code>, <code>prev</code>, <code>crossover</code>.)</div>
                  {v.block && Object.entries(v.block.params).map(([k, s]) => (
                    <div key={k} className="flex items-center gap-2"><span className="font-mono w-28">{k}</span><span className="text-gray-500">{s.kind}{typeof s.min === 'number' ? ` [${s.min}..${s.max}]` : ''} = {String(s.default)}</span></div>
                  ))}
                  {v.block?.value && <div><span className="text-gray-400">value:</span> <code className="text-[11px]">{text.match(/value:\s*(.+)/)?.[1]}</code></div>}
                  {v.block?.signal && <div><span className="text-gray-400">signal:</span> <code className="text-[11px]">{text.match(/signal:\s*(.+)/)?.[1]}</code></div>}
                </div>
              )}

              {/* live validation + budget */}
              {errs.length > 0 ? (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2"><b>Validation:</b><ul className="list-disc ml-5">{errs.slice(0, 8).map((e, i) => <li key={i}>{e}</li>)}</ul></div>
              ) : (
                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Valid TSL.</div>
              )}
              {budget && (
                <div className="text-[11px] text-gray-500 flex gap-3">
                  <span>nodes {budget.nodes}/{budget.maxNodes}</span><span>lookback {budget.lookback}/{budget.maxLookback}</span><span>state {budget.state}/{budget.maxState}</span><span>params {budget.params}/{budget.maxParams}</span>
                </div>
              )}

              {/* metadata */}
              <div className="grid grid-cols-2 gap-2">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="px-2 py-1 text-sm border rounded" />
                <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="tags, comma-separated" className="px-2 py-1 text-sm border rounded" />
              </div>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Description + risk notes" className="w-full px-2 py-1 text-sm border rounded" />

              {/* preview */}
              <div className="flex items-center gap-2">
                <button onClick={runPreview} disabled={!v.block} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"><Play className="w-3.5 h-3.5" /> Preview</button>
                {preview && <span className="text-[11px] text-gray-500">last values: {preview.value.slice(-6).map((x) => (Number.isFinite(x) ? x.toFixed(3) : 'NaN')).join(', ')}{preview.signals.length ? ` · signals: ${preview.signals.slice(-6).map((s) => (s ? 'T' : 'F')).join('')}` : ''}</span>}
              </div>

              {report && !report.publicVisible.passed && (
                <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-2"><div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="w-4 h-4" /> Gate failed:</div><ul className="list-disc ml-5">{report.publicVisible.results.filter((r) => !r.passed).map((r, i) => <li key={i}>{r.gate}: {r.detail}</li>)}</ul></div>
              )}
              {pubErr && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{pubErr}</div>}
            </div>

            <div className="flex justify-end gap-2 px-4 py-3 border-t">
              <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={onPublish} disabled={busy || errs.length > 0} title={errs.length ? 'Fix validation errors first' : undefined} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                <UploadCloud className="w-4 h-4" /> {busy ? 'Gating…' : 'Publish'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TslBlockStudio;
